import type { ChecklistItemFragment, RegistryChecklistSectionFragment, RegistryChecklistFragment } from './RegistryChecklist.graphql.generated';
import {
  RegistryChecklistSectionFragmentDoc,
  GetRegistryChecklistDocument,
  RegistryChecklistFragmentDoc,
  useGetRegistryChecklistQuery,
  useGetRegistryChecklistSectionByIdLazyQuery,
  useUpdateRegistryChecklistItemMutation,
  useCreateRegistryChecklistFromTemplateMutation,
} from './RegistryChecklist.graphql.generated';
import { ChecklistTemplateEnum, CatalogStorefront } from '@graphql/generated-base';
import { useCallback, useState, useMemo, useEffect } from 'react';
import { useRegistryChecklistTelemetry } from './RegistryChecklist.telemetry';
import { useShopStorefrontContext } from '../Catalog/components/ShopStorefrontProvider';

const CHECKLIST_ITEM_TYPE_NAME: ChecklistItemFragment['__typename'] = 'RegistryChecklistItem';
const REGISTRY_CHECKLIST_TYPE_NAME: RegistryChecklistFragment['__typename'] = 'RegistryChecklist';
const CHECKLIST_SECTION_TYPE_NAME: RegistryChecklistSectionFragment['__typename'] = 'RegistryChecklistSection';

export const useRegistryChecklistController = (eventId: string) => {
  const [openedSectionId, setOpenedSectionId] = useState('');
  const { currentStorefront } = useShopStorefrontContext();
  const [getSectionById, { data: sectionByIdData, loading: getSectionLoading }] = useGetRegistryChecklistSectionByIdLazyQuery({ batchMode: 'fast' });
  const [updateChecklistItem] = useUpdateRegistryChecklistItemMutation();
  const [createChecklist, { data: createChecklistData, loading: createChecklistLoading }] = useCreateRegistryChecklistFromTemplateMutation();
  const { onChecklistSectionExpandClicked, onChecklistItemCheckClicked } = useRegistryChecklistTelemetry();

  const { data, loading: getChecklistLoading } = useGetRegistryChecklistQuery({ variables: { eventId }, batchMode: 'fast' });
  const checklist = data?.getRegistryChecklist;

  const handleOnToggle = useCallback(
    (sectionId: string, sectionTitle: string) => {
      if (openedSectionId === sectionId) {
        setOpenedSectionId('');
      } else {
        getSectionById({ variables: { id: sectionId } });
        setOpenedSectionId(sectionId);
        onChecklistSectionExpandClicked({ section: sectionTitle });
      }
    },
    [getSectionById, openedSectionId, onChecklistSectionExpandClicked],
  );

  const initChecklist = useCallback(() => {
    createChecklist({
      variables: {
        payload: {
          eventId,
          checklistTemplate: currentStorefront === CatalogStorefront.baby ? ChecklistTemplateEnum.defaultBabyChecklist : ChecklistTemplateEnum.defaultWeddingChecklist,
        },
      },
      update: (cache, { data }) => {
        const queryBaseProps = {
          query: GetRegistryChecklistDocument,
          variables: { eventId },
        };

        // Create `getRegistryChecklist` cache entry with the new checklist
        // This way users can start interacting with the checklist's cache without refreshing the page
        cache.writeQuery({
          ...queryBaseProps,
          data: {
            getRegistryChecklist: data?.createRegistryChecklistFromTemplate,
          },
        });
      },
    });
  }, [createChecklist, eventId, currentStorefront]);

  useEffect(() => {
    if (getChecklistLoading) {
      return;
    }

    if (!checklist) {
      initChecklist();
    }
  }, [checklist, initChecklist, getChecklistLoading]);

  const handleOnCheckItem = useCallback(
    (item: ChecklistItemFragment, isChecked: boolean, sectionId: string) => {
      onChecklistItemCheckClicked({ state: isChecked ? 'checked' : 'unchecked', item: item.title });
      updateChecklistItem({
        variables: {
          id: item.id,
          payload: { isChecked },
        },
        optimisticResponse: {
          updateRegistryChecklistItem: {
            __typename: CHECKLIST_ITEM_TYPE_NAME,
            id: item.id,
            isCheckable: item.isCheckable,
            isChecked,
          },
        },
        update: cache => {
          if (!checklist) {
            return;
          }

          const section = checklist.checklistSections.find(section => section.id === sectionId);

          if (!section) {
            return;
          }

          const cacheIdentifiedChecklist = cache.identify(checklist);
          const cacheIdentifiedSection = cache.identify(section);

          if (!cacheIdentifiedChecklist || !cacheIdentifiedSection) {
            return;
          }

          const cacheChecklist = cache.readFragment<RegistryChecklistFragment>({
            id: cacheIdentifiedChecklist,
            fragment: RegistryChecklistFragmentDoc,
            fragmentName: REGISTRY_CHECKLIST_TYPE_NAME,
          });

          const cacheSection = cache.readFragment<RegistryChecklistSectionFragment>({
            id: cacheIdentifiedSection,
            fragment: RegistryChecklistSectionFragmentDoc,
            fragmentName: CHECKLIST_SECTION_TYPE_NAME,
          });

          if (!cacheChecklist || !cacheSection) {
            return;
          }

          const previousChecklistCheckedCount = cacheChecklist.checklistProgress?.checkedCount ?? 0;
          const previousSectionCheckedCount = cacheSection.sectionProgress?.checkedCount ?? 0;

          cache.writeFragment({
            id: cacheIdentifiedChecklist,
            fragment: RegistryChecklistFragmentDoc,
            fragmentName: REGISTRY_CHECKLIST_TYPE_NAME,
            data: {
              ...cacheChecklist,
              checklistProgress: {
                ...cacheChecklist.checklistProgress,
                checkedCount: isChecked ? previousChecklistCheckedCount + 1 : previousChecklistCheckedCount - 1,
              },
            },
          });

          cache.writeFragment({
            id: cacheIdentifiedSection,
            fragment: RegistryChecklistSectionFragmentDoc,
            fragmentName: CHECKLIST_SECTION_TYPE_NAME,
            data: {
              ...cacheSection,
              sectionProgress: {
                ...cacheSection.sectionProgress,
                checkedCount: isChecked ? previousSectionCheckedCount + 1 : previousSectionCheckedCount - 1,
              },
            },
          });
        },
      });
    },
    [updateChecklistItem, checklist, onChecklistItemCheckClicked],
  );

  const progress = useMemo(() => {
    return checklist?.checklistProgress || createChecklistData?.createRegistryChecklistFromTemplate.checklistProgress;
  }, [checklist, createChecklistData]);

  return {
    progressPercentage: progress ? Math.round((progress.checkedCount / progress.totalCount) * 100) : 0,
    checklistSections: checklist?.checklistSections,
    handleOnToggle,
    openedSectionId,
    sectionData: sectionByIdData?.getRegistryChecklistSectionById,
    handleOnCheckItem,
    getSectionLoading,
    createChecklistLoading,
    checklistLoading: getChecklistLoading || createChecklistLoading,
  };
};
